@if (!isMobile()) {
  <div
    [class]="`${TASK_STATUS_DISPLAY[subTask().status].borderLeftClass} flex w-full items-center gap-2.5 border-l-[6px]! border-default bg-proffeo-surface px-3 py-3 transition-default hover:bg-proffeo-surface-muted`"
  >
    <div class="flex min-w-0 w-full items-center gap-4 justify-between">
      <!-- Takes the remaining width: wraps (long words too) to two lines, then ends with an ellipsis. -->
      <p
        [matTooltip]="subTask().name"
        class="line-clamp-2 min-w-0 flex-1 wrap-anywhere"
      >
        {{ subTask().name }}
      </p>

      <!-- Narrow rows (isCompact) use mini controls: status dot, priority flag, progress percentage. -->
      <div
        [class.gap-3]="isCompact()"
        [class.gap-8]="!isCompact()"
        class="flex shrink-0 justify-end"
      >
        <div
          [class.gap-2]="isCompact()"
          [class.gap-4]="!isCompact()"
          class="flex items-center justify-start"
        >
          <proffeo-task-status-select
            (statusSelected)="statusChanged.emit($event)"
            (click)="$event.stopPropagation()"
            [mode]="TaskStatusSelectEnum.DOTS"
            [taskStatus]="subTask().status"
            [small]="true"
            [showLabel]="!isCompact()"
            [readonly]="false"
          ></proffeo-task-status-select>

          <proffeo-task-priority-select
            (click)="$event.stopPropagation()"
            (prioritySelected)="priorityChanged.emit($event)"
            [taskPriority]="subTask().priority"
            [small]="isCompact()"
            [readonly]="false"
          ></proffeo-task-priority-select>
        </div>

        <div class="flex items-center gap-1">
          <proffeo-task-timer
            [mode]="taskTimerMode.readonly"
            [summaryTime]="$safeNavigationMigration(subTask().statistics?.summaryTime)"
            [estimation]="$safeNavigationMigration(subTask().estimation?.time)"
          ></proffeo-task-timer>
        </div>

        <div class="flex items-center gap-1">
          <proffeo-task-progress-select
            (progressChange)="percentageChange.emit($event)"
            (click)="$event.stopPropagation()"
            [progress]="subTask().percentageOfProgress ?? 0"
            [density]="isCompact() ? 'compact' : 'comfortable'"
          />
        </div>

        <div
          (click)="$event.stopPropagation()"
          [matMenuTriggerFor]="subtaskActions"
          class="flex items-center justify-start cursor-pointer"
        >
          <mat-icon fontIcon="more_vert" />
        </div>
      </div>
    </div>
  </div>
}

@if (isMobile()) {
  <div
    [class]="`border-l-[6px]! ${TASK_STATUS_DISPLAY[subTask().status].borderLeftClass}`"
    class="border-hover relative box-border flex w-full max-w-none min-w-0 cursor-pointer flex-col gap-3 px-3 py-1.5"
  >
    <div class="flex w-full min-w-0 flex-col gap-1.5 py-2">
      <div class="w-full min-w-0">
        <p class="line-clamp-2 w-full break-words">
          {{ subTask().name }}
        </p>
      </div>

      <div class="flex w-full min-w-0 flex-wrap items-center justify-between gap-2">
        <div>
          <proffeo-task-status-select
            (click)="$event.stopPropagation()"
            [mode]="TaskStatusSelectEnum.DOTS"
            [taskStatus]="subTask().status"
            [small]="true"
            [readonly]="true"
          ></proffeo-task-status-select>
        </div>

        <div class="mt-0.5">
          <proffeo-task-priority-select
            (prioritySelected)="priorityChanged.emit($event)"
            (click)="$event.stopPropagation()"
            [taskPriority]="subTask().priority"
            [small]="false"
            [readonly]="false"
          ></proffeo-task-priority-select>
        </div>

        @if (subTask().subtasksCount > 0) {
          <div class="flex items-center gap-1">
            <mat-icon
              fontIcon="subdirectory_arrow_right"
              class="opacity-50 icon-md"
            />
            <p class="text-sm">{{ subTask().subtasksCount }}</p>
          </div>
        }

        <div class="flex items-center gap-1">
          <proffeo-task-timer
            [mode]="taskTimerMode.readonly"
            [summaryTime]="$safeNavigationMigration(subTask().statistics?.summaryTime)"
            [estimation]="$safeNavigationMigration(subTask().estimation?.time)"
          ></proffeo-task-timer>
        </div>

        <div class="flex items-center gap-1">
          <proffeo-task-progress-select
            (progressChange)="percentageChange.emit($event)"
            (click)="$event.stopPropagation()"
            [progress]="subTask().percentageOfProgress ?? 0"
            density="compact"
          />
        </div>
      </div>
    </div>
  </div>
}

<mat-menu #subtaskActions="matMenu">
  <button
    (click)="addTimeReport.emit(subTask())"
    class="group w-full py-3 gap-2 px-2 cursor-pointer flex items-center hover:bg-proffeo-accent hover:text-proffeo-on-action transition-150 transition-all"
  >
    <mat-icon
      fontIcon="timer"
      class="group-hover:icon-white"
    />
    <p>{{ 'task-menu.add-time-report' | translate }}</p>
  </button>
  <button
    (click)="copySubtaskLink()"
    class="group w-full py-3 gap-2 px-2 cursor-pointer flex items-center hover:bg-proffeo-accent hover:text-proffeo-on-action transition-150 transition-all"
  >
    <mat-icon
      fontIcon="content_copy"
      class="group-hover:icon-white"
    />
    <p>{{ 'task-menu.copy-link' | translate }}</p>
  </button>
  <button
    (click)="copySubtaskNumber()"
    class="group w-full py-3 gap-2 px-2 cursor-pointer flex items-center hover:bg-proffeo-accent hover:text-proffeo-on-action transition-150 transition-all"
  >
    <mat-icon
      fontIcon="content_copy"
      class="group-hover:icon-white"
    />
    <p>{{ 'task-menu.copy-number' | translate }}</p>
  </button>
  <button
    (click)="deleteSubtask()"
    class="group w-full py-3 gap-2 px-2 cursor-pointer flex items-center hover:bg-proffeo-accent hover:text-proffeo-on-action transition-150 transition-all"
  >
    <mat-icon
      fontIcon="deleteTask"
      class="group-hover:icon-white"
    />
    <p>{{ 'task-menu.delete-task' | translate }}</p>
  </button>
</mat-menu>
